<script setup lang="ts">
/**
 * Univerzální modální komponenta pro potvrzovací a informační dialogy
 * Implementace pomocí HeadlessUI Dialog
 */
import { useI18n } from 'vue-i18n'
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { IconCheck, IconTrash, IconChevronLeft } from '@tabler/icons-vue'
import AppButton from './AppButton.vue'

const { t } = useI18n()

defineProps<{
  title: string;
  message: string;
  isOpen: boolean;
  confirmText?: string;
  cancelText?: string;
  confirmType?: 'primary' | 'danger';
  type?: 'confirm' | 'alert';
}>();

const emit = defineEmits(['confirm', 'cancel']);
</script>

<template>
  <TransitionRoot as="template" :show="isOpen">
    <Dialog as="div" class="relative z-50" @close="emit('cancel')">
      <TransitionChild 
        as="template" 
        enter="ease-out duration-200" 
        enter-from="opacity-0" 
        enter-to="opacity-100" 
        leave="ease-in duration-200" 
        leave-from="opacity-100" 
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" aria-hidden="true" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto p-4">
        <div class="flex min-h-full items-center justify-center">
          <TransitionChild 
            as="template" 
            enter="ease-out duration-200" 
            enter-from="opacity-0 scale-95" 
            enter-to="opacity-100 scale-100" 
            leave="ease-in duration-200" 
            leave-from="opacity-100 scale-100" 
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel class="relative bg-base-300 border border-white/[0.06] rounded-xl shadow-xl w-full max-w-md mx-auto p-5 flex flex-col gap-4 transform transition-all">
              <!-- Titulek -->
              <DialogTitle class="text-lg font-semibold text-gray-200">
                {{ title }}
              </DialogTitle>

              <!-- Obsah -->
              <div class="text-gray-400">
                {{ message }}
              </div>

              <!-- Tlačítka -->
              <div class="flex justify-end gap-2 mt-2">
                <!-- Pro alert dialogy zobrazíme jen jedno tlačítko -->
                <template v-if="type === 'alert'">
                  <AppButton
                    variant="primary"
                    icon-only
                    :title="confirmText || 'OK'"
                    @click="emit('confirm')"
                  >
                    <template #icon>
                      <IconCheck />
                    </template>
                  </AppButton>
                </template>

                <!-- Pro potvrzovací dialogy zobrazíme dvě tlačítka -->
                <template v-else>
                  <AppButton
                    variant="gray"
                    icon-only
                    :title="cancelText || t('common.buttons.cancel')"
                    @click="emit('cancel')"
                  >
                    <template #icon>
                      <IconChevronLeft />
                    </template>
                  </AppButton>
                  <AppButton
                    :variant="confirmType === 'danger' ? 'danger' : 'primary'"
                    icon-only
                    :title="confirmText || t('common.buttons.confirm')"
                    @click="emit('confirm')"
                  >
                    <template #icon>
                      <IconTrash v-if="confirmType === 'danger'" />
                      <IconCheck v-else />
                    </template>
                  </AppButton>
                </template>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>